import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_TUTORIAL_STEPS } from "./tutorialSteps";

const EditorTutorialContext = createContext(null);

export const useEditorTutorial = () => {
  const ctx = useContext(EditorTutorialContext);
  if (!ctx) {
    throw new Error("useEditorTutorial must be used within an EditorTutorialProvider");
  }
  return ctx;
};

export const EditorTutorialProvider = ({ children, steps = EDITOR_TUTORIAL_STEPS }) => {
  const tutorial = useEditorTutorialState({ steps });
  return (
    <EditorTutorialContext.Provider value={tutorial}>
      {children}
    </EditorTutorialContext.Provider>
  );
};

const useEditorTutorialState = ({ steps }) => {
  const stepsRef = useRef(steps);
  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Metadata useful for integrations (Roadmap 2) and debugging
  const sessionSeqRef = useRef(0);
  const [sessionId, setSessionId] = useState(0);
  const [startedAt, setStartedAt] = useState(null);

  // Prevent multi-advance when a step is auto-advancing via an external event
  const autoAdvanceLockRef = useRef(false);

  const lastStepIndex = Math.max((steps?.length || 1) - 1, 0);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const start = useCallback(() => {
    sessionSeqRef.current += 1;
    setSessionId(sessionSeqRef.current);
    setStartedAt(Date.now());
    autoAdvanceLockRef.current = false;
    setStepIndex(0);
    setIsOpen(true);
  }, []);

  const goTo = useCallback(
    (index) => {
      const nextIndex = Math.min(Math.max(Number(index) || 0, 0), lastStepIndex);
      setStepIndex(nextIndex);
    },
    [lastStepIndex],
  );

  const next = useCallback(() => {
    setStepIndex((prev) => Math.min(prev + 1, lastStepIndex));
  }, [lastStepIndex]);

  const prev = useCallback(() => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  // Clear auto-advance lock when the step changes (or the tutorial opens)
  useEffect(() => {
    if (!isOpen) return;
    autoAdvanceLockRef.current = false;
  }, [isOpen, stepIndex]);

  const currentStep = isOpen ? (steps?.[stepIndex] || null) : null;

  const canGoPrev = isOpen && stepIndex > 0;
  const canGoNext = isOpen && stepIndex < lastStepIndex;

  // Direct signals from integration code (optional).
  // Example: tutorial.signal("clicked-element") to advance Step 1 without relying on selection heuristics.
  const signal = useCallback(
    (name, detail) => {
      if (!isOpen) return false;
      const step = stepsRef.current?.[stepIndex];
      const expected = step?.advanceOnSignal;
      if (!expected || expected !== name) return false;

      if (typeof step?.canAdvance === "function") {
        try {
          const ok = step.canAdvance({ name, detail });
          if (!ok) return false;
        } catch (_) {
          return false;
        }
      }

      if (autoAdvanceLockRef.current) return true;
      autoAdvanceLockRef.current = true;
      next();
      return true;
    },
    [isOpen, next, stepIndex],
  );

  // Window event-based auto-advance (e.g. Step 1 waiting for a click on an element).
  useEffect(() => {
    if (!isOpen) return;
    const step = steps?.[stepIndex];
    const eventName = step?.advanceOnEvent;
    if (!eventName) return;

    const handler = (evt) => {
      if (autoAdvanceLockRef.current) return;
      if (typeof step?.advanceOnEventPredicate === "function") {
        try {
          const ok = step.advanceOnEventPredicate(evt);
          if (!ok) return;
        } catch (_) {
          return;
        }
      }
      autoAdvanceLockRef.current = true;
      next();
    };

    window.addEventListener(eventName, handler);
    return () => {
      window.removeEventListener(eventName, handler);
    };
  }, [isOpen, next, stepIndex, steps]);

  return useMemo(
    () => ({
      // state
      isOpen,
      steps,
      stepIndex,
      currentStep,
      sessionId,
      startedAt,

      // actions
      start,
      close,
      next,
      prev,
      goTo,
      signal,

      // derived
      canGoPrev,
      canGoNext,
      lastStepIndex,
    }),
    [
      isOpen,
      steps,
      stepIndex,
      currentStep,
      sessionId,
      startedAt,
      start,
      close,
      next,
      prev,
      goTo,
      signal,
      canGoPrev,
      canGoNext,
      lastStepIndex,
    ],
  );
};

